import { useForm } from "@tanstack/react-form"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { CalendarClock } from "lucide-react"
import { useEffect, useMemo } from "react"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import {
  LabeledSelect,
  type LabeledSelectOption,
} from "@/components/ui/labeled-select"
import { formatWeekendRange } from "@/core/guild-war/weekend-dates"
import { guildWarApi } from "@/lib/guild-war-api"
import { WEEK_STATES, type WeekState } from "@/lib/guild-war-constants"
import { guildWarKeys } from "@/lib/query-keys"
import {
  usePendingTransitionWeekId,
  useTransitionMessage,
  type WeeksUiStore,
} from "@/lib/stores/weeks-ui-store"

const weekStateOptions = WEEK_STATES.map((state) => ({
  value: state,
  label: state,
})) as LabeledSelectOption<WeekState>[]

const nextWeekState: Readonly<Record<WeekState, WeekState | null>> = {
  draft: "signup_open",
  signup_open: "planning",
  planning: "completed",
  completed: null,
}

type ManualTransitionCardProps = {
  weeks: Array<{ id: string; state: WeekState; weekStartAt: string }>
  store: WeeksUiStore
}

export function ManualTransitionCard({
  weeks,
  store,
}: ManualTransitionCardProps) {
  const queryClient = useQueryClient()
  const pendingWeekId = usePendingTransitionWeekId(store)
  const transitionMessage = useTransitionMessage(store)

  const transitionWeekMutation = useMutation({
    mutationFn: ({ weekId, toState }: { weekId: string; toState: WeekState }) =>
      guildWarApi.transitionWeek(weekId, toState),
    onMutate: ({ weekId }) => {
      store.getState().setPendingTransitionWeekId(weekId)
      store.getState().setTransitionMessage(null)
    },
    onSuccess: async () => {
      store.getState().setPendingTransitionWeekId(null)
      await queryClient.invalidateQueries({ queryKey: guildWarKeys.weeks() })
    },
    onError: (error) => {
      store.getState().setPendingTransitionWeekId(null)
      store.getState().setTransitionMessage({
        tone: "error",
        text:
          error instanceof Error
            ? error.message
            : "Failed to transition week state",
      })
    },
  })

  const transitionForm = useForm({
    defaultValues: {
      weekId: weeks[0]?.id ?? "",
      toState: "signup_open" as WeekState,
    },
    onSubmit: ({ value }) => {
      if (!value.weekId) return
      transitionWeekMutation.mutate({
        weekId: value.weekId,
        toState: value.toState,
      })
    },
  })

  useEffect(() => {
    if (!weeks.length) {
      transitionForm.setFieldValue("weekId", "")
      return
    }

    const currentWeekId = transitionForm.state.values.weekId
    if (currentWeekId && weeks.some((week) => week.id === currentWeekId)) {
      return
    }

    transitionForm.setFieldValue("weekId", weeks[0]?.id ?? "")
  }, [weeks, transitionForm])

  useEffect(() => {
    const selectedWeekId = transitionForm.state.values.weekId
    if (!selectedWeekId) return

    const selectedWeek = weeks.find((week) => week.id === selectedWeekId)
    if (!selectedWeek) return

    const suggestedState = nextWeekState[selectedWeek.state]
    if (suggestedState) {
      transitionForm.setFieldValue("toState", suggestedState)
    }
  }, [weeks, transitionForm.state.values.weekId, transitionForm])

  const weekOptions = useMemo(
    () =>
      weeks.map((week) => ({
        value: week.id,
        label: `${formatWeekendRange({ weekStartAt: week.weekStartAt })} (${week.state})`,
      })),
    [weeks],
  )
  return (
    <Card>
      <CardHeader>
        <CardHeading icon={<CalendarClock className="size-5 text-primary" />}>
          Manual Transition
        </CardHeading>
        <CardDescription>
          Jump directly to a target state when you need to correct a workflow or
          fast-track a known valid transition.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <transitionForm.Field name="weekId">
            {(field) => (
              <div className="grid gap-1.5">
                <Label>Week</Label>
                <LabeledSelect
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value ?? "")}
                  options={weekOptions}
                  className="w-44"
                  placeholder="Select week"
                />
              </div>
            )}
          </transitionForm.Field>

          <transitionForm.Field name="toState">
            {(field) => (
              <div className="grid gap-1.5">
                <Label>Target State</Label>
                <LabeledSelect
                  value={field.state.value}
                  onValueChange={(value) =>
                    field.handleChange(value as WeekState)
                  }
                  options={weekStateOptions}
                  className="w-44"
                  placeholder="Select state"
                />
              </div>
            )}
          </transitionForm.Field>

          <transitionForm.Subscribe
            selector={(state) => ({
              weekId: state.values.weekId,
              toState: state.values.toState,
            })}
          >
            {({ weekId, toState }) => {
              const wk = weeks.find((w) => w.id === weekId)
              return (
                <Button
                  disabled={
                    !weekId || pendingWeekId !== null || wk?.state === toState
                  }
                  onClick={() => void transitionForm.handleSubmit()}
                  type="button"
                >
                  Transition Week
                </Button>
              )
            }}
          </transitionForm.Subscribe>
        </div>
        {transitionMessage ? (
          <p className="mt-3 text-sm text-destructive">
            {transitionMessage.text}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}
